/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import { useCallback } from 'react';
import { GuidUtils, ObjectUtils } from "../Utils";

/**
 * Finds an item in a list by its key
 * @param {string|number} key - The key to search for
 * @param {Array} items - The array to search in
 * @param {string} primaryKeyName - The name of the primary key field
 * @returns {object|undefined} - The found item or undefined
 */
export const findItemByKey = (key, items, primaryKeyName) => {
    const stringKey = String(key);
    return items.find((x) => String(x[primaryKeyName]) === stringKey) ||
        items.find((x) => String(x['_tempId']) === stringKey);
};

/**
 * Finds the index of an item in a list
 * @param {string|number|object} keyOrObject - The key or object to find
 * @param {Array} items - The array to search in
 * @param {string} primaryKeyName - The name of the primary key field
 * @returns {number} - The index of the item or -1 if not found
 */
export const findItemIndex = (keyOrObject, items, primaryKeyName) => {
    const stringKeyValue = (typeof keyOrObject === 'object') ?
        String(keyOrObject?.[primaryKeyName] ?? keyOrObject?.['_tempId']) :
        String(keyOrObject);
    
    const item = findItemByKey(stringKeyValue, items, primaryKeyName);
    return items.indexOf(item);
};

/**
 * Custom hook for managing lists of items
 * @param {Array} items - The array of items
 * @param {Function} updateItems - Function to update the array
 * @param {String} primaryKeyName - The name of the primary key field
 * @param {Object} options - Additional options
 * @returns {Object} - Methods for managing the list
 */
export const useListManagement = (items, updateItems, primaryKeyName, options = {}) => {
    const {
        orderableField = null,
        newItemDefaults = null,
    } = options;
    
    const updateItem = useCallback((keyOrObject) => {
        let newItems = items ? [...items] : [];
        newItems[findItemIndex(keyOrObject, newItems, primaryKeyName)] = keyOrObject;
        updateItems(newItems);
    }, [items, updateItems, primaryKeyName]);
    
    const removeItem = useCallback((keyOrObject) => {
        let newItems = items ? [...items] : [];
        newItems.splice(findItemIndex(keyOrObject, newItems, primaryKeyName), 1);
        updateItems(newItems);
    }, [items, updateItems, primaryKeyName]);
    
    const addItem = useCallback((item = null) => {
        let newItem = item ?? newItemDefaults ?? {};
        let newItems = items ? ObjectUtils.Clone(items) : [];
        
        newItem._tempId = GuidUtils.Generate();
        
        if (orderableField) {
            let maxOrder = 0;
            items?.forEach((x) => {
                if (x[orderableField] > maxOrder) {
                    maxOrder = x[orderableField];
                }
            });
            
            newItem[orderableField] = maxOrder + 1;
        }
        
        newItems.push(newItem);
        updateItems(newItems);
    }, [items, updateItems, newItemDefaults, orderableField]);
    
    return [
        addItem,
        updateItem,
        removeItem,
    ];
};